/**
 * @fileoverview TransactionFormContainer - Adapter Component
 *
 * Acts as an adapter between the main App component and the TransactionForm.
 * Handles complex prop mapping, function wrapping, and state management for
 * transaction forms including support for template-based income allocation.
 *
 * @module components/TransactionForm/TransactionFormContainer
 */
import { useMemo, useEffect } from 'react';
import { useBudgetTemplate } from '../../hooks/useBudgetTemplate';
import { useMonthlyAllocations } from '../../hooks/useMonthlyAllocations';
import { useCategoryBalances } from '../../hooks/useCategoryBalances';
import TransactionForm from './TransactionForm';

/**
 * TransactionFormContainer - Adapter Component
 *
 * This component acts as an adapter between the main App component and the TransactionForm.
 * It handles the complex prop mapping and function wrapping needed for the form.
 *
 * Props:
 * - transactionHook (object): Transaction management hook
 * - onClose (function): Optional - Called after successful transaction submission (for modal usage)
 * - hideSubmitButton (boolean): Optional - if true, hides the submit button (for modal usage)
 * - singleBankMode (boolean): Whether budget is in single bank mode
 */
function TransactionFormContainer({ transactionHook, budgetStartMonth, onClose, hideSubmitButton = false, singleBankMode = false }) {

    // Extract month from transaction date for template availability check
    const transactionMonth = useMemo(() => {
        if (!transactionHook.date) return null;
        const [year, month] = transactionHook.date.split('-');
        return `${year}-${month}`;
    }, [transactionHook.date]);

    // Check template availability for the transaction date's month
    const templateCheck = useBudgetTemplate(transactionMonth);

    // Get current allocations for the transaction month
    const { allocations: transactionMonthAllocations } = useMonthlyAllocations(
        transactionMonth,
        templateCheck.budgetTemplate
    );

    // Get category balances for Phase 0 (fill negative categories first)
    const { categoryBalances } = useCategoryBalances(transactionMonth);

    // Automatically turn off template when changing to a month without template
    useEffect(() => {
        if (transactionHook.useTemplateForIncome && templateCheck.noTemplate && !templateCheck.isLoading) {
            transactionHook.setUseTemplateForIncome(false);
            transactionHook.setTransactionCategories([{ category: '', amount: '' }]);
        }
    }, [templateCheck.noTemplate, templateCheck.isLoading, transactionHook]);

    // Re-allocate when date changes and template is enabled
    useEffect(() => {
        if (transactionHook.useTemplateForIncome &&
            transactionHook.amount &&
            parseFloat(transactionHook.amount) > 0 &&
            templateCheck.budgetTemplate &&
            !templateCheck.isLoading &&
            transactionMonthAllocations !== undefined) { // Wait for allocations to load

            const allocations = allocateIncomeByTransactionMonthTemplate(parseFloat(transactionHook.amount));
            transactionHook.setTransactionCategories(allocations.map(alloc => ({
                category: alloc.category,
                amount: alloc.amount
            })));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transactionMonth, templateCheck.budgetTemplate, templateCheck.isLoading, transactionMonthAllocations]); // Re-run when month, template, or allocations change

    // Helper function to allocate income using the transaction date's month template
    // Implements three-phase allocation: fill negative first (if enabled), fill to targets, then distribute excess by percentages
    const allocateIncomeByTransactionMonthTemplate = (totalAmount) => {
        if (!templateCheck.budgetTemplate) return [];

        const allocations = [];
        let remainingAmount = totalAmount;

        // Get current allocations for this month (default to empty if not loaded yet)
        // Deep copy to avoid mutating the original transactionMonthAllocations
        const currentAllocations = {};
        if (transactionMonthAllocations) {
            for (const [key, value] of Object.entries(transactionMonthAllocations)) {
                currentAllocations[key] = { ...value };
            }
        }

        // Get allocation settings from template
        const fillNegativeFirst = templateCheck.fillNegativeFirst || false;
        const allowOverTarget = templateCheck.allowOverTarget || false;

        // PHASE 0: Fill negative balances first (if enabled)
        if (fillNegativeFirst && remainingAmount > 0.01 && categoryBalances) {
            for (const [category, settings] of Object.entries(templateCheck.budgetTemplate)) {
                if (remainingAmount <= 0.01) break;

                // Find the balance for this category
                const balance = categoryBalances[category];
                if (!balance) continue;

                const endingBalance = balance.endingBalance || 0;
                if (endingBalance >= 0) continue; // Skip positive balances

                const negativeAmount = Math.abs(endingBalance);
                let fillAmount;

                if (allowOverTarget) {
                    // Fill entire negative balance
                    fillAmount = Math.min(negativeAmount, remainingAmount);
                } else {
                    // Cap at remaining target space (target minus what's already allocated this month)
                    // This ensures negative categories just "jump ahead" in priority but still respect targets
                    const target = settings.target || 0;
                    const alreadyAllocated = currentAllocations[category]?.allocated || 0;
                    const targetRemaining = Math.max(0, target - alreadyAllocated);
                    fillAmount = Math.min(negativeAmount, targetRemaining, remainingAmount);
                }

                if (fillAmount > 0.01) {
                    allocations.push({ category, amount: fillAmount.toFixed(2) });
                    remainingAmount = Math.round((remainingAmount - fillAmount) * 100) / 100;

                    // Track toward target for Phase 1
                    if (!currentAllocations[category]) {
                        currentAllocations[category] = { allocated: 0 };
                    }
                    currentAllocations[category].allocated += fillAmount;
                }
            }
        }

        // PHASE 1: Fill categories by priority group (sequential processing)
        // Group categories by priorityGroup
        const priorityGroups = {};
        for (const [category, settings] of Object.entries(templateCheck.budgetTemplate)) {
            const group = settings.priorityGroup || 1;
            if (!priorityGroups[group]) priorityGroups[group] = [];
            priorityGroups[group].push({ category, ...settings });
        }

        // Sort groups ascending (1, 2, 3, ...)
        const sortedGroupNumbers = Object.keys(priorityGroups).map(Number).sort((a, b) => a - b);

        // Process each priority group sequentially
        for (const groupNumber of sortedGroupNumbers) {
            const groupCategories = priorityGroups[groupNumber];

            // Calculate space remaining in this group
            const categoriesWithSpace = [];
            let totalSpaceRemaining = 0;

            for (const categoryInfo of groupCategories) {
                const alreadyAllocated = currentAllocations[categoryInfo.category]?.allocated || 0;
                const spaceRemaining = Math.max(0, categoryInfo.target - alreadyAllocated);

                if (spaceRemaining > 0) {
                    categoriesWithSpace.push({
                        category: categoryInfo.category,
                        spaceRemaining,
                        targetAmount: categoryInfo.target,
                        percentage: categoryInfo.percentage
                    });
                    totalSpaceRemaining += spaceRemaining;
                }
            }

            // Allocate proportionally within this group
            if (categoriesWithSpace.length > 0 && totalSpaceRemaining > 0) {
                const amountToDistribute = Math.min(remainingAmount, totalSpaceRemaining);

                categoriesWithSpace.forEach((categoryInfo, index) => {
                    let amountForCategory;

                    if (index === categoriesWithSpace.length - 1) {
                        // Last category gets remainder (handles rounding)
                        amountForCategory = Math.min(remainingAmount, categoryInfo.spaceRemaining);
                    } else {
                        // Proportional allocation based on space remaining
                        const proportion = categoryInfo.spaceRemaining / totalSpaceRemaining;
                        amountForCategory = Math.min(
                            Math.round(proportion * amountToDistribute * 100) / 100,
                            categoryInfo.spaceRemaining,
                            remainingAmount
                        );
                    }

                    if (amountForCategory > 0.01) {
                        // Check if category already has an allocation (from Phase 0)
                        const existingAllocation = allocations.find(a => a.category === categoryInfo.category);
                        if (existingAllocation) {
                            // Add to existing allocation
                            const newAmount = parseFloat(existingAllocation.amount) + amountForCategory;
                            existingAllocation.amount = newAmount.toFixed(2);
                        } else {
                            allocations.push({
                                category: categoryInfo.category,
                                amount: amountForCategory.toFixed(2)
                            });
                        }
                        remainingAmount = Math.round((remainingAmount - amountForCategory) * 100) / 100;
                    }
                });

                // CRITICAL: Stop if funds exhausted
                if (remainingAmount <= 0.01) break;

                // CRITICAL: Stop if this group's targets weren't fully met
                // (Don't process next group unless current group is 100% satisfied)
                if (amountToDistribute < totalSpaceRemaining) break;
            }
        }

        // PHASE 2: Distribute any remaining amount by percentages
        if (remainingAmount > 0.01) {
            const categoriesWithPercentages = Object.entries(templateCheck.budgetTemplate)
                .filter(([, settings]) => settings.percentage > 0);

            if (categoriesWithPercentages.length > 0) {
                const totalPercentage = categoriesWithPercentages.reduce(
                    (sum, [, settings]) => sum + settings.percentage, 0
                );

                if (totalPercentage > 0) {
                    let percentageAmountRemaining = remainingAmount;

                    categoriesWithPercentages.forEach(([category, settings], index) => {
                        if (percentageAmountRemaining <= 0.01) return;

                        let percentageAmount;
                        if (index === categoriesWithPercentages.length - 1) {
                            // Last category gets whatever is left
                            percentageAmount = percentageAmountRemaining;
                        } else {
                            percentageAmount = Math.round(
                                (settings.percentage / totalPercentage) * remainingAmount * 100
                            ) / 100;
                        }

                        // Add to existing allocation or create new one
                        const existingAllocation = allocations.find(a => a.category === category);
                        if (existingAllocation) {
                            const newAmount = parseFloat(existingAllocation.amount) + percentageAmount;
                            existingAllocation.amount = newAmount.toFixed(2);
                        } else {
                            allocations.push({
                                category,
                                amount: percentageAmount.toFixed(2)
                            });
                        }

                        percentageAmountRemaining = Math.round(
                            (percentageAmountRemaining - percentageAmount) * 100
                        ) / 100;
                    });
                }
            }
        }

        return allocations;
    };

    // Handle field changes
    const handleFieldChange = (fieldName, value) => {
        switch (fieldName) {
            case 'amount':
                transactionHook.setAmount(value);
                break;
            case 'description':
                transactionHook.setDescription(value);
                break;
            case 'date':
                transactionHook.setDate(value);
                break;
            case 'currentUserKey':
                transactionHook.setCurrentUserKey(value);
                break;
            case 'paidDirectly':
                transactionHook.setPaidDirectly(value);
                break;
            case 'useTemplateForIncome':
                transactionHook.setUseTemplateForIncome(value);
                // If turning on template, allocate current amount using transaction month's template
                if (value && transactionHook.amount && parseFloat(transactionHook.amount) > 0) {
                    const allocations = allocateIncomeByTransactionMonthTemplate(parseFloat(transactionHook.amount));
                    transactionHook.setTransactionCategories(allocations.map(alloc => ({
                        category: alloc.category,
                        amount: alloc.amount
                    })));
                } else if (!value) {
                    // If turning off template, reset to single empty category
                    transactionHook.setTransactionCategories([{ category: '', amount: '' }]);
                }
                break;
            case 'adjustmentFromCategory':
                transactionHook.setAdjustmentFromCategory(value);
                break;
            case 'adjustmentToCategory':
                transactionHook.setAdjustmentToCategory(value);
                break;
            case 'templateAmount':
                // Handle template income amount changes using transaction month's template
                if (value && parseFloat(value) > 0) {
                    const allocations = allocateIncomeByTransactionMonthTemplate(parseFloat(value));
                    transactionHook.setTransactionCategories(allocations.map(alloc => ({
                        category: alloc.category,
                        amount: alloc.amount
                    })));
                } else {
                    transactionHook.setTransactionCategories([{ category: '', amount: '' }]);
                }
                break;
            default:
                // Unknown field
                break;
        }
    };

    // Handle category changes
    const handleCategoryChange = (action, index, field, value) => {
        switch (action) {
            case 'add':
                transactionHook.addCategoryRow();
                break;
            case 'remove':
                transactionHook.removeCategoryRow(index);
                break;
            case 'update':
                transactionHook.updateTransactionCategory(index, field, value);
                break;
            default:
                // Unknown category action
                break;
        }
    };

    // Handle form submission
    const handleSubmit = async () => {
        const success = await transactionHook.addTransaction();
        if (success && onClose) {
            onClose();
        }
    };

    // Handle form cancellation / change type
    const handleCancel = () => {
        transactionHook.resetForm();
        transactionHook.setErrors({});
    };

    return (
        <TransactionForm
            transactionType={transactionHook.transactionType}
            amount={transactionHook.amount}
            description={transactionHook.description}
            date={transactionHook.date}
            transactionCategories={transactionHook.transactionCategories}
            currentUserKey={transactionHook.currentUserKey}
            paidDirectly={transactionHook.paidDirectly}
            useTemplateForIncome={transactionHook.useTemplateForIncome}
            adjustmentFromCategory={transactionHook.adjustmentFromCategory}
            adjustmentToCategory={transactionHook.adjustmentToCategory}
            categories={transactionHook.categories}
            regularCategories={transactionHook.regularCategories}
            users={transactionHook.users}
            budgetStartMonth={budgetStartMonth}
            errors={transactionHook.errors}
            onFieldChange={handleFieldChange}
            onCategoryChange={handleCategoryChange}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            getAvailableCategories={transactionHook.getAvailableCategories}
            getAvailableCategoriesForExpenses={transactionHook.getAvailableCategoriesForExpenses}
            getAvailableCategoriesForTransfers={transactionHook.getAvailableCategoriesForTransfers}
            isGrandfatheredCategory={transactionHook.isGrandfatheredCategory}
            handlePositiveNumberInput={transactionHook.handlePositiveNumberInput}
            clearError={transactionHook.clearError}
            clearCategoryError={transactionHook.clearCategoryError}
            hideSubmitButton={hideSubmitButton}
            singleBankMode={singleBankMode}
            isEdit={transactionHook.editingId !== null}
            hasTemplateForTransactionMonth={!templateCheck.noTemplate && !templateCheck.isLoading}
        />
    );
}

export default TransactionFormContainer;